import { Inline, Stack, Text, textVariants, vars } from '@etg/wings';
import { css } from '@eti/styles';
import { useDateFormat, useTranslation } from '@eti/providers';
import { date } from '@eti/utils';
import TimelineWrapper from '../../../common/TimelineWrapper';
import TimelineRow from '../common/TimelineRow';
import Time from '../../../../../../common/time/Time';

const arrivalDateStyles = (isDateChanged: boolean) => css`
  background-color: ${isDateChanged ? vars.colors.signal.caution.background : 'none'};
`;

const originalArrivalDateStyles = css`
  color: #737373;
  text-decoration: line-through;
`;

const timelineRowStyles = (isDisplayedFirst: boolean, isDisplayedLast: boolean) => css`
  background-color: ${vars.colors.signal.information.tint};
  border-radius: ${isDisplayedFirst ? `${vars.card.borderRadius} ${vars.card.borderRadius}` : '0 0'}
    ${isDisplayedLast ? `${vars.card.borderRadius} ${vars.card.borderRadius}` : '0 0'};
  color: ${vars.colors.signal.information.dark};
`;

interface ItineraryChangeOfDateProps {
  arrivalDate: string;
  durationInDays: number;
  isDashed?: boolean;
  isDisplayedFirst?: boolean;
  isDisplayedLast?: boolean;
  originalArrivalDate?: string | null;
}

const ItineraryChangeOfDate = ({
  arrivalDate,
  durationInDays,
  isDashed = false,
  isDisplayedFirst = false,
  isDisplayedLast = false,
  originalArrivalDate,
}: ItineraryChangeOfDateProps) => {
  const { t } = useTranslation();
  const { weekdayYear } = useDateFormat();
  const daysOrDayTranslation = durationInDays > 1 ? t('Days') : t('Day');
  const durationInDaysText = `+${durationInDays} ${daysOrDayTranslation}`;
  const formattedArrivalDate = date.format(arrivalDate, weekdayYear);
  const formattedOriginalArrivalDate = originalArrivalDate
    ? date.format(originalArrivalDate, weekdayYear)
    : null;
  const isDateChanged =
    Boolean(formattedOriginalArrivalDate) && formattedArrivalDate !== formattedOriginalArrivalDate;

  const borderColor = isDashed ? vars.colors.inputs.border : vars.colors.decoration.primary;
  const borderStyle = isDashed ? 'dashed' : 'solid';

  return (
    <TimelineRow
      className={timelineRowStyles(isDisplayedFirst, isDisplayedLast)}
      data-testid="itinerary-change-of-date"
    >
      <TimelineWrapper
        borderColorAfter={borderColor}
        borderColorBefore={borderColor}
        borderStyleAfter={borderStyle}
        borderStyleBefore={borderStyle}
        childSize={0}
        wrapperSize={20}
      />
      <Stack align="start" spacing={0}>
        <Inline spacing={16}>
          <Text data-testid="itinerary-change-of-date-duration" variant={textVariants._16_BOLD}>
            {durationInDaysText}
          </Text>

          <Text
            as={Time}
            className={arrivalDateStyles(isDateChanged)}
            data-testid="itinerary-change-of-date-arrival-date"
            date={arrivalDate}
            format="date"
            variant={textVariants._16_BOLD}
          >
            {formattedArrivalDate}
          </Text>
        </Inline>
        {originalArrivalDate && isDateChanged && (
          <Text
            as={Time}
            className={originalArrivalDateStyles}
            data-testid="itinerary-change-of-date-original-arrival-date"
            date={originalArrivalDate}
            format="date"
            variant={textVariants._16_BOLD}
          >
            {formattedOriginalArrivalDate}
          </Text>
        )}
      </Stack>
    </TimelineRow>
  );
};

export default ItineraryChangeOfDate;
